// src/AppRouter.tsx - Modern Tailwind router configuration
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ThemeToggle from './components/ThemeToggle';
import Sidebar from './components/Sidebar';
import FirebaseLoginForm from './components/FirebaseLoginForm';
import ChatInterface from './components/ChatInterface';
import CodeDisplay from './components/CodeDisplay';
import UserStatsComponent from './components/UserStats';
import DashboardPreview from './components/DashboardPreview';
import ChatOnlyPage from './pages/ChatOnlyPage';
import RedeemPage from './pages/RedeemPage';
import ChatHistoryPage from './pages/ChatHistoryPage';
import SettingsPage from './pages/SettingsPage';
import ProjectsPage from './pages/ProjectsPage';
import WorkflowPage from './pages/WorkflowPage';
import NewWorkflowPage from './pages/NewWorkflowPage';
import CodebookPage from './pages/CodebookPage';
import ArtifactsPage from './pages/ArtifactsPage';
import LandingPage from './pages/LandingPage';
import LandingPageUS from './pages/LandingPageUS';
import LandingPageIndia from './pages/LandingPageIndia';
import LandingPageLATAM from './pages/LandingPageLATAM';
import LandingPageGCC from './pages/LandingPageGCC';
import SharedCodebookPage from './pages/SharedCodebookPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import GuidePage from './pages/GuidePage';
import RunCodeOnlinePage from './pages/RunCodeOnlinePage';
import CapstonePage from './pages/CapstonePage';
import CapstoneProjectPage from './pages/CapstoneProjectPage';
import MLmatePage from './pages/MLmatePage';
import LearningPathsPage from './pages/LearningPathsPage';
import LearningPathGraphPage from './pages/LearningPathGraphPage';
import PayPage from './pages/PayPage';
import PayThanksPage from './pages/PayThanksPage';
import HomePage from './pages/HomePage';
import StartPage from './pages/StartPage';
import PathPage from './pages/PathPage';
import WebinarThanksPage from './pages/WebinarThanksPage';
import WebinarAugPage from './pages/WebinarAugPage';
import WebinarAugThanksPage from './pages/WebinarAugThanksPage';
import BuildCoursePage from './pages/BuildCoursePage';
import BuildCourseThanksPage from './pages/BuildCourseThanksPage';
import MachineLearningPage from './pages/MachineLearningPage';

import { chatApi } from './services/api';
import { Message, CodeBlock, Attachment } from './types/chat';
import { useState, useEffect, useCallback } from 'react';
import { getRandomTagline } from './utils/taglines';
import './App.css';

// The SINGLE owner of the post-login destination. FirebaseLoginForm never
// navigates after a successful login — it dispatches 'auth-token-saved',
// AuthContext flips isAuthenticated, and the /login route swaps to this
// component (two competing redirects both consuming post_login_next was
// issue #142). Resolution order: ?next= from the email-link continue URL
// (survives other browsers/devices) → post_login_next in localStorage
// (same-browser detours like /redeem) → /codemate.
const PostLoginRedirect: React.FC = () => {
  const [dest, setDest] = useState<string | null>(null);
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('next');
    const stored = localStorage.getItem('post_login_next');
    localStorage.removeItem('post_login_next');
    const next = param || stored;
    // Functional update: StrictMode re-runs this effect after the key was
    // already consumed — never downgrade an already-resolved destination.
    setDest(prev => prev ?? (next && next.startsWith('/') ? next : '/codemate'));
  }, []);
  return dest ? <Navigate to={dest} replace /> : null;
};

// Geo-based landing page selector — detects country and shows the right page
const GeoLandingPage: React.FC = () => {
  const [LandingComponent, setLandingComponent] = useState<React.FC | null>(null);

  useEffect(() => {
    const detectGeo = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${API_URL}/api/payment/geo`);
        const data = await response.json();
        const country = (data.country_code || '').toUpperCase();

        if (country === 'IN') {
          const { default: Page } = await import('./pages/LandingPageIndia');
          setLandingComponent(() => Page);
        } else if (['MX', 'BR', 'AR', 'CO', 'CL', 'PE'].includes(country)) {
          const { default: Page } = await import('./pages/LandingPageLATAM');
          setLandingComponent(() => Page);
        } else if (['AE', 'SA', 'OM', 'BH', 'KW', 'QA'].includes(country)) {
          const { default: Page } = await import('./pages/LandingPageGCC');
          setLandingComponent(() => Page);
        } else {
          const { default: Page } = await import('./pages/LandingPageUS');
          setLandingComponent(() => Page);
        }
      } catch {
        // Default to US on any error
        const { default: Page } = await import('./pages/LandingPageUS');
        setLandingComponent(() => Page);
      }
    };
    detectGeo();
  }, []);

  if (!LandingComponent) {
    // Brief loading while geo detects — show dark background to avoid flash
    return <div className="min-h-screen bg-gray-950" />;
  }

  return <LandingComponent />;
};

// Referral redirect — saves code to localStorage and redirects to login
const ReferralRedirect: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  React.useEffect(() => {
    if (code) {
      localStorage.setItem('referral_code', code);
      console.log('[REFERRAL] Code saved:', code);
    }
  }, [code]);
  return <Navigate to={`/login?ref=${code}`} replace />;
};

// Protected Workspace Component (formerly DashboardPage)
const WorkspacePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { sessionId?: string; messages?: Message[]; codeBlocks?: CodeBlock[] } | null;
  const { isAuthenticated } = useAuth();

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [codeBlocks, setCodeBlocks] = useState<CodeBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentMessageId, setCurrentMessageId] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState<'execution' | 'preview'>('execution');
  const [tagline] = useState<string>(getRandomTagline()); // Get random tagline once per session
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true); // Sidebar state - default collapsed

  // Track if current response is a dashboard (set by onComplete callback)
  const [isDashboardResponse, setIsDashboardResponse] = useState(false);

  // Track the current intent for this session
  const [currentIntent, setCurrentIntent] = useState<string | null>(null);

  // Check if current code blocks contain HTML
  const hasHTMLCode = codeBlocks.some(block =>
    block.language?.toLowerCase() === 'html'
  );

  // Check if code blocks contain dashboard-related Python code
  const hasDashboardCode = codeBlocks.some(block => {
    const code = block.code?.toLowerCase() || '';
    const isPython = ['python', 'py'].includes(block.language?.toLowerCase() || '');
    return isPython && (
      code.includes('html') ||
      code.includes('dashboard') ||
      code.includes('chart') ||
      code.includes('plot') ||
      code.includes('matplotlib') ||
      code.includes('plotly') ||
      code.includes('bokeh') ||
      code.includes('kpi') ||
      (code.includes('print(') && (code.includes('<') || code.includes('<!doctype')))
    );
  });

  // Determine if Preview tab should be enabled
  // Preview is enabled if: HTML code OR dashboard intent OR dashboard-related Python code
  const shouldEnablePreview = hasHTMLCode || isDashboardResponse || hasDashboardCode;

  // Determine if Execute tab should be enabled
  // Execute is disabled if it's ONLY a dashboard/HTML response
  const shouldEnableExecute = !(hasHTMLCode && !hasDashboardCode) && !isDashboardResponse;

  // Determine if we should show right panel at all (only for code/dashboard requests)
  const shouldShowRightPanel = codeBlocks.length > 0 || shouldEnablePreview || shouldEnableExecute;

  // Log layout changes
  useEffect(() => {
    console.log('[WORKSPACE LAYOUT] shouldShowRightPanel:', shouldShowRightPanel);
    console.log('[WORKSPACE LAYOUT] codeBlocks.length:', codeBlocks.length);
    console.log('[WORKSPACE LAYOUT] currentIntent:', currentIntent);
    if (shouldShowRightPanel) {
      console.log('[WORKSPACE LAYOUT] 📐 Split-view mode (Chat + Code/Dashboard)');
    } else {
      console.log('[WORKSPACE LAYOUT] 💬 Chat-only mode (Full width)');
    }
  }, [shouldShowRightPanel, codeBlocks.length, currentIntent]);

  // Auto-switch tabs based on content type
  useEffect(() => {
    // If dashboard content detected, switch to Preview
    if (shouldEnablePreview && (hasHTMLCode || isDashboardResponse || hasDashboardCode)) {
      console.log('[WORKSPACE] useEffect detected dashboard content - auto-switching to Preview');
      console.log('  - hasHTMLCode:', hasHTMLCode);
      console.log('  - isDashboardResponse:', isDashboardResponse);
      console.log('  - hasDashboardCode:', hasDashboardCode);
      console.log('  - Current activeTab:', activeTab);

      if (activeTab !== 'preview') {
        setActiveTab('preview');
        console.log('[WORKSPACE] ✅ Tab switched to preview via useEffect');
      }
    }
    // If regular code (not dashboard), switch to Execute
    else if (codeBlocks.length > 0 && !shouldEnablePreview && shouldEnableExecute) {
      console.log('[WORKSPACE] Regular code detected - switching to execution tab');
      if (activeTab !== 'execution') {
        setActiveTab('execution');
        console.log('[WORKSPACE] ✅ Tab switched to execution via useEffect');
      }
    }
  }, [hasHTMLCode, isDashboardResponse, hasDashboardCode, shouldEnablePreview, shouldEnableExecute, activeTab, codeBlocks.length]);

  // Load session from sessionStorage on mount
  useEffect(() => {
    if (isAuthenticated) {
      const savedSessionId = sessionStorage.getItem('currentSessionId'); // Session-specific storage
      const savedMessages = sessionStorage.getItem('chatMessages'); // Session-specific storage

      if (savedSessionId && savedMessages) {
        console.log('[WORKSPACE] Restoring session from sessionStorage:', savedSessionId);
        setCurrentSessionId(savedSessionId);

        // Parse and restore messages (convert timestamp strings back to Date objects)
        const parsedMessages = JSON.parse(savedMessages).map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        setMessages(parsedMessages);

        // Extract code blocks from messages
        const extractedCodeBlocks: CodeBlock[] = [];
        parsedMessages.forEach((msg: any) => {
          if (msg.type === 'system' && msg.content) {
            const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
            let match;
            while ((match = codeBlockRegex.exec(msg.content)) !== null) {
              extractedCodeBlocks.push({
                language: match[1] || 'text',
                code: match[2].trim()
              });
            }
          }
        });

        if (extractedCodeBlocks.length > 0) {
          setCodeBlocks(extractedCodeBlocks);
        }

        console.log('[WORKSPACE] Restored', parsedMessages.length, 'messages and', extractedCodeBlocks.length, 'code blocks');
      }
    }
  }, [isAuthenticated]);

  // Save session to sessionStorage whenever it changes
  useEffect(() => {
    if (currentSessionId && messages.length > 0) {
      console.log('[WORKSPACE] Saving session to sessionStorage:', currentSessionId, 'with', messages.length, 'messages');
      sessionStorage.setItem('currentSessionId', currentSessionId);
      sessionStorage.setItem('chatMessages', JSON.stringify(messages));
    }
  }, [currentSessionId, messages, codeBlocks]);

  // Memoize startNewSession
  const startNewSession = useCallback(async () => {
    try {
      const session = await chatApi.startSession();
      setCurrentSessionId(session.session_id);
      setMessages([]);
      setCodeBlocks([]);

      // Clear saved session from sessionStorage
      console.log('[WORKSPACE] Starting new session:', session.session_id);
      sessionStorage.removeItem('currentSessionId');
      sessionStorage.removeItem('chatMessages');
    } catch (error) {
      console.error('Failed to start session:', error);
    }
  }, []);

  // Start a new session when user is authenticated OR restore from navigation state
  useEffect(() => {
    if (state?.sessionId && state?.messages) {
      console.log('[WORKSPACE] Restoring session from navigation state');
      console.log('[WORKSPACE] Code blocks from state:', state.codeBlocks);
      // Restore session from ChatOnlyPage navigation
      setCurrentSessionId(state.sessionId);
      setMessages(state.messages);

      // Restore code blocks if provided
      if (state.codeBlocks && state.codeBlocks.length > 0) {
        console.log('[WORKSPACE] Setting code blocks:', state.codeBlocks);
        setCodeBlocks(state.codeBlocks);
        return; // Don't extract again
      }

      // Extract code blocks from messages
      const extractedCodeBlocks: CodeBlock[] = [];
      state.messages.forEach(msg => {
        if (msg.type === 'system' && msg.content) {
          // Find all code blocks in the message
          const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
          let match;
          while ((match = codeBlockRegex.exec(msg.content)) !== null) {
            extractedCodeBlocks.push({
              language: match[1] || 'text',
              code: match[2].trim()
            });
          }
        }
      });

      if (extractedCodeBlocks.length > 0) {
        setCodeBlocks(extractedCodeBlocks);
        setActiveTab('execution'); // Switch to execution tab
      }
    } else if (isAuthenticated && !currentSessionId) {
      // Check if there's a saved session in sessionStorage
      const savedSessionId = sessionStorage.getItem('currentSessionId');
      if (!savedSessionId) {
        // No saved session, start a new one
        startNewSession();
      }
    }
  }, [isAuthenticated, currentSessionId, startNewSession, state]);

  const handleSendMessage = async (messageText: string, attachments?: Attachment[]) => {
    if (!currentSessionId) {
      console.error('No active session');
      return;
    }

    // Reset dashboard flag for new request
    setIsDashboardResponse(false);

    // Note: We DON'T clear code blocks here anymore.
    // They will be updated by the onComplete callback based on the new response.
    // If new response has no code, onComplete will receive empty array and panel will hide.

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageText,
      timestamp: new Date(),
      attachments: attachments,
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    const systemMessageId = (Date.now() + 1).toString();
    const systemMessage: Message = {
      id: systemMessageId,
      type: 'system',
      content: '',
      timestamp: new Date(),
    };

    // Don't add empty message - just show loading state in ChatInterface
    // setMessages(prev => [...prev, systemMessage]);
    setCurrentMessageId(systemMessageId);

    let streamedContent = '';
    let messageAdded = false;

    try {
      await chatApi.sendMessageStream(
        currentSessionId,
        { message: messageText, attachments },
        (chunk: string) => {
          streamedContent += chunk;

          // Add message on first chunk
          if (!messageAdded) {
            setMessages(prev => [...prev, { ...systemMessage, content: streamedContent }]);
            messageAdded = true;
          } else {
            // Update existing message
            setMessages(prev => prev.map(msg =>
              msg.id === systemMessageId
                ? { ...msg, content: streamedContent }
                : msg
            ));
          }
        },
        (codeBlocks: any[], isDashboard?: boolean, intent?: string) => {
          console.log('[WORKSPACE] onComplete called with code blocks:', codeBlocks);
          console.log('[WORKSPACE] Is dashboard:', isDashboard);
          console.log('[WORKSPACE] Intent:', intent);

          // Update current intent for tracking
          setCurrentIntent(intent || null);

          // Set dashboard flag in state
          setIsDashboardResponse(isDashboard || false);

          // ALWAYS update code blocks (even if empty array)
          // This is critical: if response has no code, we need to clear old code blocks
          console.log('[WORKSPACE] Setting code blocks:', codeBlocks?.length || 0, 'blocks');
          setCodeBlocks(codeBlocks || []);

          if (codeBlocks && codeBlocks.length > 0) {
            // Check if code is HTML
            const isHTML = codeBlocks.some(block =>
              block.language?.toLowerCase() === 'html'
            );

            // Check if Python code contains dashboard-related content
            const hasDashboardCode = codeBlocks.some(block => {
              const code = block.code?.toLowerCase() || '';
              const isPython = ['python', 'py'].includes(block.language?.toLowerCase() || '');
              return isPython && (
                code.includes('html') ||
                code.includes('dashboard') ||
                code.includes('chart') ||
                code.includes('kpi') ||
                (code.includes('print(') && code.includes('<'))
              );
            });

            // Auto-switch to Preview tab if:
            // 1. HTML code detected, OR
            // 2. Backend marked as dashboard (isDashboard flag), OR
            // 3. Python code contains dashboard keywords
            if (isHTML || isDashboard || hasDashboardCode) {
              console.log('📊 Dashboard detected - switching to Preview tab');
              console.log('  - isHTML:', isHTML);
              console.log('  - isDashboard flag:', isDashboard);
              console.log('  - hasDashboardCode:', hasDashboardCode);
              console.log('  - Current activeTab before switch:', activeTab);

              // Switch immediately - React will batch the state updates
              setActiveTab('preview');
              console.log('[WORKSPACE] ✅ Tab switch to preview requested');
            } else {
              // For non-dashboard code (regular code execution), switch to Execute tab
              console.log('💻 Regular code detected - switching to Execute tab');
              console.log('  - Intent:', intent);
              console.log('  - Code blocks:', codeBlocks.length);
              console.log('  - Current activeTab before switch:', activeTab);

              setActiveTab('execution');
              console.log('[WORKSPACE] ✅ Tab switch to execution requested');
            }
          }
        }
      );
    } catch (error) {
      console.error('Error sending message:', error);
      const errorContent = 'Sorry, there was an error processing your request.';
      setMessages(prev => prev.map(msg =>
        msg.id === systemMessageId
          ? { ...msg, content: errorContent }
          : msg
      ));
    } finally {
      setLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (currentSessionId) {
      try {
        await chatApi.endSession(currentSessionId);

        // Clear current session state
        setMessages([]);
        setCodeBlocks([]);
        setCurrentSessionId(null);

        // Clear sessionStorage to force Chat page to start fresh
        console.log('[WORKSPACE] Clearing session from sessionStorage');
        sessionStorage.removeItem('currentSessionId');
        sessionStorage.removeItem('chatMessages');

        // Redirect to Chat page (it will start a new session automatically)
        console.log('[WORKSPACE] Redirecting to Chat page for new session');
        navigate('/codemate');
      } catch (error) {
        console.error('Failed to end session:', error);
      }
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Fixed Sidebar */}
      <Sidebar />

      {/* Main Workspace Area */}
      <div className="flex-1 flex flex-col relative overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 dark:from-gray-900 dark:via-purple-900/20 dark:to-blue-900/20"></div>
        <div className="absolute top-0 -left-4 w-96 h-96 bg-purple-300 dark:bg-purple-700 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-30 animate-pulse-slow"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-yellow-300 dark:bg-yellow-600 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-30 animate-pulse-slower"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-pink-300 dark:bg-pink-700 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-30 animate-pulse-subtle"></div>

        {/* Glass header */}
        <header className="relative z-10 border-b border-white/20 dark:border-gray-700/30 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <img
                  src="/cubemate-logo.png"
                  alt="Cubemate Logo"
                  className="w-10 h-10 rounded-xl shadow-lg transform hover:scale-105 transition-transform"
                />
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 bg-clip-text text-transparent">Cubemate</h1>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-600 dark:text-gray-400 font-medium italic">{tagline}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <ThemeToggle />
              <button
                onClick={handleEndSession}
                className="px-5 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 text-sm font-medium"
              >
                ✨ New Session
              </button>
            </div>
          </div>
        </div>
        </header>

        <div className="flex-1 flex overflow-hidden relative z-10">
        {/* Left Panel - Chat (expands to full width when no code) */}
        <div className={`flex flex-col min-w-0 overflow-hidden bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm transition-all duration-500 ease-in-out ${
          shouldShowRightPanel ? 'w-1/2' : 'w-full'
        }`}>
          <ChatInterface
            messages={messages}
            onSendMessage={handleSendMessage}
            loading={loading}
            currentMessageId={currentMessageId}
          />
        </div>

        {/* Right Panel - Code Actions (hidden for general chat) */}
        {shouldShowRightPanel && (
          <div className="w-1/2 border-l border-white/30 dark:border-gray-700/30 bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm flex flex-col overflow-hidden transition-all duration-500 ease-in-out">
            {/* Chrome-style tabs */}
            <div className="bg-gradient-to-b from-gray-200/80 to-gray-100/80 dark:from-gray-800/80 dark:to-gray-900/80 border-b border-gray-300 dark:border-gray-700 px-2 pt-2">
                <div className="flex space-x-1">
              <button
                className={`relative px-6 py-3 text-sm font-medium transition-all ${
                  activeTab === 'execution'
                    ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-t-lg shadow-sm'
                    : shouldEnableExecute
                      ? 'bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-200/50 dark:hover:bg-gray-800/50 rounded-t-lg'
                      : 'bg-transparent text-gray-400 dark:text-gray-600 cursor-not-allowed rounded-t-lg opacity-50'
                }`}
                onClick={() => shouldEnableExecute && setActiveTab('execution')}
                disabled={!shouldEnableExecute}
                title={!shouldEnableExecute ? 'Execute is disabled for dashboard/HTML code. Use Preview instead.' : 'Execute code'}
              >
                <div className="flex items-center space-x-2">
                  <span>⚡</span>
                  <span>Execute</span>
                </div>
                {activeTab === 'execution' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-blue-500"></div>
                )}
              </button>
              <button
                className={`relative px-6 py-3 text-sm font-medium transition-all ${
                  activeTab === 'preview'
                    ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-t-lg shadow-sm'
                    : shouldEnablePreview
                      ? 'bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-200/50 dark:hover:bg-gray-800/50 rounded-t-lg'
                      : 'bg-transparent text-gray-400 dark:text-gray-600 cursor-not-allowed rounded-t-lg opacity-50'
                }`}
                onClick={() => shouldEnablePreview && setActiveTab('preview')}
                disabled={!shouldEnablePreview}
                title={!shouldEnablePreview ? 'Preview available only for HTML/dashboard code' : 'Preview dashboard output'}
              >
                <div className="flex items-center space-x-2">
                  <span>🌐</span>
                  <span>Preview</span>
                </div>
                {activeTab === 'preview' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-blue-500"></div>
                )}
              </button>
            </div>
          </div>

            <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900">
              {activeTab === 'execution' && (
                <CodeDisplay codeBlocks={codeBlocks} currentMessageId={currentMessageId} displayMode="execution" />
              )}
              {activeTab === 'preview' && shouldEnablePreview && (
                <DashboardPreview codeBlocks={codeBlocks} currentMessageId={currentMessageId} />
              )}
            </div>
            </div>
          )}
        </div>
      </div>
      {/* End of Main Workspace Area */}
    </div>
  );
};

// Stats Page Component
const StatsPage: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(true); // Default collapsed

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-h-screen bg-background overflow-auto">
        <header className="border-b border-border bg-card">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <img
                    src="/cubemate-logo.png"
                    alt="Cubemate Logo"
                    className="w-8 h-8 rounded-lg"
                  />
                  <div>
                    <h1 className="text-lg font-semibold text-foreground">Cubemate</h1>
                    <span className="text-xs text-muted-foreground">Usage Statistics</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <UserStatsComponent />
        </div>
      </div>
    </div>
  );
};

// Loading Component
const LoadingScreen: React.FC = () => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        <h2 className="text-xl font-semibold text-foreground">Loading Cubemate...</h2>
        <p className="text-muted-foreground">Initializing your AI development assistant</p>
      </div>
    </div>
  );
};

// v3.19 paywall guard — every paid surface (chat, bookmate, workflow) goes
// through here. Unauthenticated → /login. Authenticated but no active tier
// (NULL or legacy 'basic') → /pay. Otherwise render the protected page.
//
// v3.19.7: Starter is now the free trial tier. Trial users with
// subscription_status='trial_expired' have their tier blanked by the backend
// or stay as 'starter' but the credit_service paywall blocks API calls. The
// PaywallGuard catches tier=NULL; the PaywallRedirectListener catches the 402
// event from any subsequent API call.
const PaywallGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  const tier = user?.subscription_tier;
  const status = (user as any)?.subscription_status;
  if (!tier || tier === 'basic') {
    return <Navigate to="/pay" replace />;
  }
  if (status === 'trial_expired') {
    return <Navigate to="/pay?reason=trial_expired" replace />;
  }
  return <>{children}</>;
};

// v3.19.7: Inner gate for Pro/Ultimate-only surfaces (Workflow).
// Layers inside PaywallGuard. Bounces Starter users to /pay with an upsell reason.
const StarterBlockGuard: React.FC<{ children: React.ReactNode; reason?: string }> = ({ children, reason = 'workflow_requires_pro' }) => {
  const { user } = useAuth();
  if (user?.subscription_tier === 'starter') {
    return <Navigate to={`/pay?reason=${reason}`} replace />;
  }
  return <>{children}</>;
};

// Listens for `payment-required` window events from api.ts and redirects to /pay.
const PaywallRedirectListener: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const handler = () => {
      if (location.pathname !== '/pay') navigate('/pay', { replace: true });
    };
    window.addEventListener('payment-required', handler);
    return () => window.removeEventListener('payment-required', handler);
  }, [navigate, location.pathname]);
  return null;
};

// Authentication wrapper
const AuthenticatedApp: React.FC = () => {
  const { isAuthenticated, isLoading, isGuest } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
    <PaywallRedirectListener />
    <Routes>
      {/* Public routes */}
      {/* Root route: v3.20 single global home page (product-led, "code learners" positioning) */}
      <Route path="/" element={<HomePage />} />
      {/* /pricing currently points to /pay (the conversion surface already shows tiers). A
          dedicated comparison page is planned but not built yet. */}
      <Route path="/pricing" element={<Navigate to="/pay" replace />} />
      {/* Geo landing pages stay live for ads & SEO — keep accessible by direct URL. */}
      <Route path="/geo" element={<GeoLandingPage />} />
      <Route path="/start" element={<StartPage />} />
      {/* /path — MLmate+Capstone learning-path ad landing (Meta Jul2026 campaign) */}
      <Route path="/path" element={<PathPage />} />
      {/* /webinar — July2026 webinar is over; reactivated Meta ads still point here,
          so redirect to the current webinar. /webinar/thanks stays live (it's the
          old ₹9 Razorpay callback + July cohort's WhatsApp invite). */}
      <Route path="/webinar" element={<Navigate to="/webinar-aug" replace />} />
      <Route path="/webinar/thanks" element={<WebinarThanksPage />} />
      {/* /webinar-aug — ₹99 webinar ("Learn AI step by step" Aug2026) */}
      <Route path="/webinar-aug" element={<WebinarAugPage />} />
      <Route path="/webinar-aug/thanks" element={<WebinarAugThanksPage />} />
      {/* /machine-learning — ₹249/mo ML subscription + free live webinar (Oct 16).
          Payment is IN-APP: CTAs point at /login?next=/pay. The on-page form is
          lead capture (WhatsApp group + reminders), not payment. */}
      <Route path="/machine-learning" element={<MachineLearningPage />} />
      <Route path="/build-india" element={<BuildCoursePage region="india" />} />
      <Route path="/build-india/thanks" element={<BuildCourseThanksPage region="india" />} />
      <Route path="/run-code-online" element={<RunCodeOnlinePage />} />
      <Route path="/us" element={<LandingPageUS />} />
      <Route path="/india" element={<LandingPageIndia />} />
      <Route path="/latam" element={<LandingPageLATAM />} />
      <Route path="/gcc" element={<LandingPageGCC />} />
      {/* /blog is served as static prebuilt HTML (see scripts/build-blog.js);
          no SPA route on purpose — AI/SEO crawlers need real HTML files. */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/bookmate/shared/:codebookId" element={<SharedCodebookPage />} />
      <Route path="/guide" element={<GuidePage />} />
      {/* /redeem — AppSumo lifetime-deal code redemption. Outside PaywallGuard on
          purpose: an expired-trial user with a code must still get through. */}
      <Route path="/redeem" element={<RedeemPage />} />
      <Route
        path="/login"
        element={isAuthenticated ? <PostLoginRedirect /> : <FirebaseLoginForm />}
      />
      <Route
        path="/corestrat-login"
        element={<Navigate to="/login" replace />}
      />
      <Route
        path="/r/:code"
        element={<ReferralRedirect />}
      />
      <Route
        path="/register"
        element={<Navigate to="/login" replace />}
      />

      {/* Protected routes - Allow both authenticated and guest users */}
      <Route
        path="/codemate/history"
        element={isAuthenticated ? <ChatHistoryPage /> : <Navigate to="/login" replace />}
      />
      {/* Chat and Workflow use persistent mount — both stay alive when switching tabs */}
      <Route
        path="/codemate/:sessionId"
        element={<PaywallGuard><ChatOnlyPage /></PaywallGuard>}
      />
      <Route
        path="/codemate"
        element={<PaywallGuard><ChatOnlyPage /></PaywallGuard>}
      />
      <Route
        path="/workspace"
        element={<PaywallGuard><WorkspacePage /></PaywallGuard>}
      />
      <Route
        path="/projects"
        element={<PaywallGuard><ProjectsPage /></PaywallGuard>}
      />
      <Route
        path="/artifacts"
        element={<PaywallGuard><ArtifactsPage /></PaywallGuard>}
      />
      <Route
        path="/flowmate"
        element={<Navigate to="/workflow" replace />}
      />
      <Route
        path="/workflow"
        element={<PaywallGuard><StarterBlockGuard><NewWorkflowPage /></StarterBlockGuard></PaywallGuard>}
      />
      <Route
        path="/workflow/:sessionId"
        element={<PaywallGuard><StarterBlockGuard><NewWorkflowPage /></StarterBlockGuard></PaywallGuard>}
      />
      <Route
        path="/bookmate"
        element={<PaywallGuard><CodebookPage /></PaywallGuard>}
      />
      {/* MLmate — learn ML by watching it run. All tiers (incl. Starter), no credit charge. */}
      <Route
        path="/mlmate"
        element={<PaywallGuard><MLmatePage /></PaywallGuard>}
      />
      {/* Learning Paths — LLM-generated roadmap graphs. All tiers, no credit
          charge; Capstone nodes gate at click time. Note: /path (singular) is
          the unrelated public webinar-ad landing page. */}
      <Route
        path="/paths"
        element={<PaywallGuard><LearningPathsPage /></PaywallGuard>}
      />
      <Route
        path="/paths/:pathId"
        element={<PaywallGuard><LearningPathGraphPage /></PaywallGuard>}
      />
      <Route
        path="/capstone"
        element={<PaywallGuard><StarterBlockGuard reason="capstone_requires_pro"><CapstonePage /></StarterBlockGuard></PaywallGuard>}
      />
      <Route
        path="/capstone/:projectId"
        element={<PaywallGuard><StarterBlockGuard reason="capstone_requires_pro"><CapstoneProjectPage /></StarterBlockGuard></PaywallGuard>}
      />
      <Route
        path="/stats"
        element={isAuthenticated ? <StatsPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/settings"
        element={isAuthenticated ? <SettingsPage /> : <Navigate to="/login" replace />}
      />

      {/* Legacy route redirects */}
      <Route path="/chat" element={<Navigate to="/codemate" replace />} />
      <Route path="/chat-history" element={<Navigate to="/codemate/history" replace />} />
      {/* /workflow is now a direct route, not a redirect */}
      <Route path="/dashboard" element={<Navigate to="/workspace" replace />} />

      {/* v3.19 paywall — must be authenticated; no tier check (this IS the paywall) */}
      <Route
        path="/pay"
        element={isAuthenticated ? <PayPage /> : <Navigate to="/login" replace />}
      />
      {/* Post-payment success interstitial — fires the Google Ads purchase
          conversion, then auto-forwards to /codemate. */}
      <Route
        path="/pay/thanks"
        element={isAuthenticated ? <PayThanksPage /> : <Navigate to="/login" replace />}
      />

      {/* Default redirects */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

const AppRouter: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AuthenticatedApp />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default AppRouter;